/**
 * Spatial queries on lake geometry in ENU metres: point-in-polygon (with holes)
 * and nearest point on polylines, plus `queryZone` (the per-frame zone status).
 *
 * Indexes (built once per LakeENU, cached in a WeakMap):
 *  - PolygonIndex: horizontal bands; each band lists the non-horizontal edges whose
 *    y-range overlaps it. The crossing-number test on the band's edges is exactly
 *    the full test (every edge that can cross the ray y = const lies in its band).
 *  - PolylineIndex: segments grouped in chunks of consecutive vertices with a
 *    bbox each; branch and bound over the chunk bboxes.
 */
import type {
  ENU,
  LakeENU,
  LatLon,
  LineENU,
  LocalFrame,
  MultiPolygonENU,
  PolygonENU,
  RingENU,
  Vector2D,
  ZoneKind,
  ZoneStatus,
} from '../contracts';
import { normDeg } from './frame';

/** Speed limit (km/h) inside inner, outer or extended shore zones (BSV). */
export const SHORE_ZONE_SPEED_LIMIT_KMH = 10;

// ─── Point in polygon (plain, O(n)) ─────────────────────────────────────────

/** Crossing-number test for a closed flattened ring. Points on the boundary are undefined. */
export function pointInRing(ring: RingENU, x: number, y: number): boolean {
  let inside = false;
  const len = ring.length;
  if (len < 6) return false;
  let x1 = ring[len - 2];
  let y1 = ring[len - 1];
  for (let i = 0; i < len; i += 2) {
    const x2 = ring[i];
    const y2 = ring[i + 1];
    if (y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) inside = !inside;
    x1 = x2;
    y1 = y2;
  }
  return inside;
}

/** Inside the exterior ring (first) and not inside any hole. */
export function pointInPolygon(poly: PolygonENU, x: number, y: number): boolean {
  if (poly.length === 0 || !pointInRing(poly[0], x, y)) return false;
  for (let i = 1; i < poly.length; i++) if (pointInRing(poly[i], x, y)) return false;
  return true;
}

export function pointInMultiPolygon(mp: MultiPolygonENU, x: number, y: number): boolean {
  for (const poly of mp) if (pointInPolygon(poly, x, y)) return true;
  return false;
}

// ─── Indexed point in polygon ───────────────────────────────────────────────

/**
 * Banded edge index over a MultiPolygonENU. `contains` gives the same result as
 * `pointInMultiPolygon` for valid polygons (holes inside their exterior), using
 * even-odd parity per polygon.
 */
export class PolygonIndex {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  private readonly bandH: number;
  private readonly nBands: number;
  /** CSR offsets into `edges`/`polyOf`, length nBands + 1. */
  private readonly bandStart: Int32Array;
  /** x1, y1, x2, y2 per entry. */
  private readonly edges: Float64Array;
  private readonly polyOf: Int32Array;
  private readonly parity: Uint8Array;

  constructor(mp: MultiPolygonENU, bandHeight = 20) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const poly of mp) {
      for (const ring of poly) {
        for (let i = 0; i < ring.length; i += 2) {
          const x = ring[i], y = ring[i + 1];
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    this.minX = minX;
    this.minY = minY;
    this.maxX = maxX;
    this.maxY = maxY;
    this.parity = new Uint8Array(Math.max(1, mp.length));
    const span = maxY - minY;
    const nBands = span > 0 ? Math.min(1 << 16, Math.max(1, Math.ceil(span / bandHeight))) : 1;
    this.nBands = nBands;
    this.bandH = span > 0 ? span / nBands : 1;

    const counts = new Int32Array(nBands + 1);
    this.forEachEdge(mp, (b0, b1) => {
      for (let b = b0; b <= b1; b++) counts[b + 1]++;
    });
    for (let b = 0; b < nBands; b++) counts[b + 1] += counts[b];
    this.bandStart = counts;
    const total = counts[nBands];
    this.edges = new Float64Array(total * 4);
    this.polyOf = new Int32Array(total);
    const fill = counts.slice(0, nBands);
    this.forEachEdge(mp, (b0, b1, x1, y1, x2, y2, pi) => {
      for (let b = b0; b <= b1; b++) {
        const k = fill[b]++;
        this.edges[4 * k] = x1;
        this.edges[4 * k + 1] = y1;
        this.edges[4 * k + 2] = x2;
        this.edges[4 * k + 3] = y2;
        this.polyOf[k] = pi;
      }
    });
  }

  private band(y: number): number {
    const b = Math.floor((y - this.minY) / this.bandH);
    return b < 0 ? 0 : b >= this.nBands ? this.nBands - 1 : b;
  }

  private forEachEdge(
    mp: MultiPolygonENU,
    cb: (b0: number, b1: number, x1: number, y1: number, x2: number, y2: number, poly: number) => void,
  ): void {
    for (let pi = 0; pi < mp.length; pi++) {
      for (const ring of mp[pi]) {
        const len = ring.length;
        if (len < 6) continue;
        let x1 = ring[len - 2];
        let y1 = ring[len - 1];
        for (let i = 0; i < len; i += 2) {
          const x2 = ring[i];
          const y2 = ring[i + 1];
          // Horizontal edges (and the closing duplicate) never cross a horizontal ray.
          if (y1 !== y2) {
            const lo = y1 < y2 ? y1 : y2;
            const hi = y1 < y2 ? y2 : y1;
            cb(this.band(lo), this.band(hi), x1, y1, x2, y2, pi);
          }
          x1 = x2;
          y1 = y2;
        }
      }
    }
  }

  contains(x: number, y: number): boolean {
    if (!(x >= this.minX && x <= this.maxX && y >= this.minY && y <= this.maxY)) return false;
    const b = this.band(y);
    const end = this.bandStart[b + 1];
    const ed = this.edges;
    const par = this.parity;
    par.fill(0);
    for (let k = this.bandStart[b]; k < end; k++) {
      const x1 = ed[4 * k], y1 = ed[4 * k + 1], x2 = ed[4 * k + 2], y2 = ed[4 * k + 3];
      if (y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) par[this.polyOf[k]] ^= 1;
    }
    for (let i = 0; i < par.length; i++) if (par[i]) return true;
    return false;
  }
}

// ─── Nearest point on polylines ─────────────────────────────────────────────

export interface NearestPoint {
  /** Distance (m) from the query point. */
  distance: number;
  /** Nearest point in ENU. */
  e: number;
  n: number;
}

/** Brute-force nearest point on a set of polylines (reference implementation). */
export function nearestPointOnLines(lines: LineENU[], x: number, y: number): NearestPoint | null {
  let best = Infinity, be = 0, bn = 0;
  for (const line of lines) {
    if (line.length === 2) {
      const d = (line[0] - x) ** 2 + (line[1] - y) ** 2;
      if (d < best) { best = d; be = line[0]; bn = line[1]; }
    }
    for (let i = 0; i + 3 < line.length; i += 2) {
      const r = segNearest(line[i], line[i + 1], line[i + 2], line[i + 3], x, y);
      if (r < best) { best = r; be = segE; bn = segN; }
    }
  }
  return best === Infinity ? null : { distance: Math.sqrt(best), e: be, n: bn };
}

// Output slots of segNearest (avoids allocations in the hot path).
let segE = 0;
let segN = 0;

/** Squared distance from (x, y) to segment a-b; nearest point in segE/segN. */
function segNearest(ax: number, ay: number, bx: number, by: number, x: number, y: number): number {
  const dx = bx - ax, dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((x - ax) * dx + (y - ay) * dy) / l2 : 0;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  segE = ax + t * dx;
  segN = ay + t * dy;
  return (segE - x) ** 2 + (segN - y) ** 2;
}

/** Chunked bbox index over polylines for fast nearest-point queries. */
export class PolylineIndex {
  readonly vertexCount: number;
  private readonly pts: Float64Array;
  /** Per chunk: first and last vertex index (segments first..last-1). */
  private readonly cFirst: Int32Array;
  private readonly cLast: Int32Array;
  /** Per chunk: minX, minY, maxX, maxY. */
  private readonly cBox: Float64Array;
  private readonly lb: Float64Array;

  constructor(lines: LineENU[], chunkSegments = 16) {
    let nPts = 0;
    let nChunks = 0;
    for (const line of lines) {
      const v = line.length >> 1;
      if (v === 0) continue;
      nPts += v;
      nChunks += Math.max(1, Math.ceil((v - 1) / chunkSegments));
    }
    this.vertexCount = nPts;
    this.pts = new Float64Array(nPts * 2);
    this.cFirst = new Int32Array(nChunks);
    this.cLast = new Int32Array(nChunks);
    this.cBox = new Float64Array(nChunks * 4);
    this.lb = new Float64Array(nChunks);
    let off = 0;
    let c = 0;
    for (const line of lines) {
      const v = line.length >> 1;
      if (v === 0) continue;
      this.pts.set(line.subarray(0, v * 2), off * 2);
      for (let s = 0; s < Math.max(1, v - 1); s += chunkSegments) {
        const first = off + s;
        const last = off + Math.min(v - 1, s + chunkSegments);
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (let i = first; i <= last; i++) {
          const x = this.pts[2 * i], y = this.pts[2 * i + 1];
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
        this.cFirst[c] = first;
        this.cLast[c] = last;
        this.cBox[4 * c] = x0;
        this.cBox[4 * c + 1] = y0;
        this.cBox[4 * c + 2] = x1;
        this.cBox[4 * c + 3] = y1;
        c++;
      }
      off += v;
    }
  }

  get empty(): boolean {
    return this.vertexCount === 0;
  }

  /** Nearest point on any polyline, or null if the index is empty. */
  nearest(x: number, y: number): NearestPoint | null {
    const nChunks = this.cFirst.length;
    if (nChunks === 0) return null;
    const box = this.cBox;
    const lb = this.lb;
    // Pass 1: squared lower bound per chunk (distance to its bbox).
    let minLb = Infinity;
    let minC = 0;
    for (let c = 0; c < nChunks; c++) {
      const dx = Math.max(box[4 * c] - x, 0, x - box[4 * c + 2]);
      const dy = Math.max(box[4 * c + 1] - y, 0, y - box[4 * c + 3]);
      const d = dx * dx + dy * dy;
      lb[c] = d;
      if (d < minLb) { minLb = d; minC = c; }
    }
    // Seed with the most promising chunk, then only visit chunks that can beat it.
    this.best = Infinity;
    this.scanChunk(minC, x, y);
    for (let c = 0; c < nChunks; c++) {
      if (lb[c] < this.best && c !== minC) this.scanChunk(c, x, y);
    }
    return { distance: Math.sqrt(this.best), e: this.bestE, n: this.bestN };
  }

  private best = Infinity;
  private bestE = 0;
  private bestN = 0;

  private scanChunk(c: number, x: number, y: number): void {
    const p = this.pts;
    const first = this.cFirst[c];
    const last = this.cLast[c];
    if (first === last) {
      const d = (p[2 * first] - x) ** 2 + (p[2 * first + 1] - y) ** 2;
      if (d < this.best) { this.best = d; this.bestE = p[2 * first]; this.bestN = p[2 * first + 1]; }
      return;
    }
    for (let i = first; i < last; i++) {
      const d = segNearest(p[2 * i], p[2 * i + 1], p[2 * i + 2], p[2 * i + 3], x, y);
      if (d < this.best) { this.best = d; this.bestE = segE; this.bestN = segN; }
    }
  }
}

/**
 * Distance and TRUE bearing from the user (pENU / pLatLon) to a target point in
 * the frame. The frame azimuth is converted with the meridian convergence:
 * frameAz = trueAz + headingCorrectionDeg(p)  =>  trueAz = frameAz - correction.
 */
export function vectorTo(frame: LocalFrame, pENU: ENU, pLatLon: LatLon, target: NearestPoint): Vector2D {
  const de = target.e - pENU.e;
  const dn = target.n - pENU.n;
  if (de === 0 && dn === 0) return { distance: 0, bearing: 0 };
  const frameAz = (Math.atan2(de, dn) * 180) / Math.PI;
  return { distance: target.distance, bearing: normDeg(frameAz - frame.headingCorrectionDeg(pLatLon)) };
}

/** Nearest point on `lines` as distance + true bearing (brute force; use a PolylineIndex for hot paths). */
export function nearestOnLines(
  lines: LineENU[],
  frame: LocalFrame,
  pENU: ENU,
  pLatLon: LatLon,
): Vector2D | null {
  const q = nearestPointOnLines(lines, pENU.e, pENU.n);
  return q ? vectorTo(frame, pENU, pLatLon, q) : null;
}

// ─── Lake query ─────────────────────────────────────────────────────────────

export interface LakeQueryIndex {
  inner: PolygonIndex;
  outer: PolygonIndex;
  extended: PolygonIndex;
  coverage: PolygonIndex;
  lakeOutline: PolygonIndex;
  shore: PolylineIndex;
  limit: PolylineIndex;
}

const indexCache = new WeakMap<LakeENU, LakeQueryIndex>();

/** Build (once) and return the query index of a lake. Call after loading to avoid a hitch on the first query. */
export function prepareLake(lake: LakeENU): LakeQueryIndex {
  let ix = indexCache.get(lake);
  if (!ix) {
    ix = {
      inner: new PolygonIndex(lake.inner),
      outer: new PolygonIndex(lake.outer),
      extended: new PolygonIndex(lake.extended),
      coverage: new PolygonIndex(lake.coverage),
      lakeOutline: new PolygonIndex(lake.lakeOutline),
      shore: new PolylineIndex(lake.shoreLines),
      limit: new PolylineIndex(lake.limitLines),
    };
    indexCache.set(lake, ix);
  }
  return ix;
}

/**
 * Zone status at the user position.
 *  - inside coverage: inner > outer > open; `extended` if inside an extended zone;
 *  - outside coverage: 'nodata' on the lake outline, else 'land' (extended = false).
 * Speed limit 10 km/h inside inner, outer or extended. `shore` / `limit` are the
 * nearest points of shoreLines / limitLines for every zone kind (null if the lake
 * has no such lines); the UI decides whether to show them for 'nodata' / 'land'.
 */
export function queryZone(lake: LakeENU, pENU: ENU, pLatLon: LatLon): ZoneStatus {
  const ix = prepareLake(lake);
  const x = pENU.e;
  const y = pENU.n;
  let zone: ZoneKind;
  let extended = false;
  if (ix.coverage.contains(x, y)) {
    zone = ix.inner.contains(x, y) ? 'inner' : ix.outer.contains(x, y) ? 'outer' : 'open';
    extended = ix.extended.contains(x, y);
  } else {
    zone = ix.lakeOutline.contains(x, y) ? 'nodata' : 'land';
  }
  const shore = ix.shore.nearest(x, y);
  const limit = ix.limit.nearest(x, y);
  return {
    lakeId: lake.meta.id,
    lakeName: lake.meta.name,
    zone,
    extended,
    extendedNote: extended ? lake.extendedNote : null,
    speedLimitKmh: zone === 'inner' || zone === 'outer' || extended ? SHORE_ZONE_SPEED_LIMIT_KMH : null,
    shore: shore ? vectorTo(lake.frame, pENU, pLatLon, shore) : null,
    limit: limit ? vectorTo(lake.frame, pENU, pLatLon, limit) : null,
  };
}
